import $ from 'jquery';
import { captureScenario } from './capture';
import SaveResultsModal from './modal';

/*
 * Issue #227: mounts the Save Results button into each calculator pane and opens the modal.
 *
 * The button is injected by JS rather than added to the eight .hbs templates. One reason is
 * maintenance -- sixteen edits versus one -- but the deciding reason is AC-3's requirement that the
 * mechanism be generic so future calculators inherit it. A slot resolver that matches on the shared
 * layout classes does that; sixteen hand-placed buttons do not.
 *
 * Per-tab placement, from the approved mockup:
 *   Basic         -- beneath the results panel
 *   Advanced      -- centred below the charts (that pane has no side results panel)
 *   Amortization  -- beside the existing Download CSV button
 *
 * Styling uses button-cmg button-small button-secondary from shared-resources/buttons.scss, which
 * ships inside every brand's {BrandCode}.css already themed, so the treatment follows all 25 brands
 * without touching a single per-brand stylesheet. button-secondary is the per-brand *secondary*
 * token ($secondary-color, CMG teal #138890) required by requirements FR-1.1 and the v2 mockup's
 * .cmg-btn.teal. Deliberately NOT button-tertiary ($tertiary-color, aqua #32a4ac): that is what the
 * Download CSV button uses, and the mockup shows Save Results sitting beside Download CSV in a
 * visibly different colour, which is the whole point of the amortization placement.
 */

const BUTTON_MARKER = 'data-save-results-open';

const ENVELOPE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"'
    + ' width="15" height="15" aria-hidden="true" focusable="false">'
    + '<rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="m2 7 10 6 10-6"></path></svg>';

function buttonHtml() {
    return '<button type="button" class="button-cmg button-small button-secondary save-results-button"'
        + ' calculator-save-results ' + BUTTON_MARKER + '>'
        + ENVELOPE_ICON + '<span>Save Results</span></button>';
}

function alreadyMounted($container) {
    return $container.find('[' + BUTTON_MARKER + ']').length > 0;
}

/**
 * Finds where the button belongs in one pane, or returns null when the pane has no slot yet
 * (for example the amortization table has not been rendered).
 */
function resolveSlot($pane, paneId) {
    if (paneId === 'amortization') {
        // The Download CSV button and its wrapper are produced by table-template.hbs on every
        // calculate, so this slot appears and disappears with the table. pairClass marks the CSV
        // button as one half of the pair so the shared sizing in calculator.scss applies ONLY when
        // Save Results actually mounts beside it -- calculator.scss ships in main.css and
        // CMGCalc.css on every brand and the partner embed, where the feature is off and the CSV
        // button must keep its natural box (PR #269 review).
        const $csv = $pane.find('#csvBtn');
        return $csv.length ? { $anchor: $csv, mode: 'after', pairClass: 'save-results-paired' } : null;
    }

    if (paneId === 'advanced') {
        // Advanced runs full width with the charts beneath, so the button sits centred after the
        // results/chart block.
        const $results = $pane.find('.results').last();
        if ($results.length) return { $anchor: $results, mode: 'after-centered' };
    }

    // Basic: beneath the results panel. Two layouts exist -- .panel--right (results beside the form)
    // and .panel--bottom (results below it) -- and both are handled by anchoring to whichever is
    // present.
    const $panel = $pane.find('.panel--right, .panel--bottom').last();
    if ($panel.length) return { $anchor: $panel, mode: 'append' };

    // Last resort: the calculate row, which every pane of every calculator has.
    const $calcRow = $pane.find('.panel--button').last();
    return $calcRow.length ? { $anchor: $calcRow, mode: 'after' } : null;
}

function mountPane($pane) {
    const paneId = ($pane.attr('id') || '').toLowerCase();
    const slot = resolveSlot($pane, paneId);

    if (!slot) return;

    const $existingScope = slot.mode === 'append' ? slot.$anchor : slot.$anchor.parent();
    if (alreadyMounted($existingScope)) return;

    const $button = $(buttonHtml());

    if (slot.mode === 'append') {
        slot.$anchor.append($('<div class="save-results-slot"></div>').append($button));
    } else if (slot.mode === 'after-centered') {
        slot.$anchor.after($('<div class="save-results-slot save-results-slot--centered"></div>').append($button));
    } else {
        if (slot.pairClass) {
            // Re-applied on every mount: the anchor is re-rendered per calculate, so the class
            // arrives fresh alongside the re-injected button.
            slot.$anchor.addClass(slot.pairClass);
        }
        slot.$anchor.after($button);
    }
}

/**
 * Injects the button into every pane of a calculator. Idempotent, so it is safe to call after each
 * calculate -- which is necessary, because the amortization table (and therefore its Download CSV
 * slot) is re-rendered every time.
 */
export function mountSaveResultsButtons(calculatorRoot) {
    if (!calculatorRoot || !calculatorRoot.length) return;

    calculatorRoot.find('.tab-pane').each((i, pane) => mountPane($(pane)));
}

/**
 * Reads the Save Results configuration the server rendered onto the calculator section.
 *
 * Returns null when the feature is off or the attributes are absent -- which is the case for the
 * homepage calculator partials and the external embed bundle, both out of scope per the issue's
 * Boundaries. That check is what keeps the button off those surfaces even though they share this
 * bundle.
 */
export function readSaveResultsContext(calculatorRoot) {
    const $section = calculatorRoot.closest('[data-render-link-calc]');

    if (!$section.length || $section.attr('data-save-results') !== 'true') {
        return null;
    }

    const saveResultsUrl = $section.attr('data-save-results-url');
    const calculatorSlug = $section.attr('data-calculator-slug');

    if (!saveResultsUrl || !calculatorSlug) return null;

    return {
        saveResultsUrl,
        calculatorSlug,
        calculatorDisplayName: $section.attr('data-calculator-display-name') || 'Calculator',
        recaptchaSiteKey: $section.attr('data-recaptcha-site-key') || '',
        // Reported to analytics only. The server never trusts these -- it resolves the entry context
        // and brand from the request route and host (AC-8).
        entryContext: $section.attr('data-entry-context') || 'corp',
        brand: $section.attr('data-brand-code') || '',
        ownerName: $section.attr('data-owner-name') || '',
        // Issue #295, AC-16: the brand-specific TCPA disclosure, rendered server-side into an inert
        // <template> by PageTemplate.cshtml. Read as innerHTML rather than text -- it carries the two
        // policy anchors. The modal holds no copy of this wording: CalculatorConsentDisclosure.cs is
        // the single source, so a Compliance revision is one edit there.
        //
        // Empty string when the template is absent, which the modal treats as "render the checkbox
        // with no disclosure beneath it". Consent is still required in that state, because AC-17
        // makes the tick a submit precondition on every brand with no exceptions and the server
        // enforces it independently -- a missing disclosure must not become a way to skip consent.
        //
        // Looked up document-wide, NOT with $section.find(). The template deliberately sits outside
        // [data-render-link-calc] because CMGCalc renders into that section and replaces its
        // children, so a template inside it is gone by the time this runs (see PageTemplate.cshtml).
        // Document-wide is no wider in practice: only PageTemplate emits this element, and only when
        // the feature flag is on, so the homepage partials and the partner embed still have nothing
        // to find.
        consentDisclosureHtml: ($('[data-save-results-disclosure]').first().prop('innerHTML') || '').trim()
    };
}

/**
 * Wires Save Results into one calculator instance.
 *
 * @param {jQuery} calculatorRoot the [data-calc-mode] element
 * @returns {function|null} a re-mount callback to call after each calculate, or null when the feature
 *                          is not enabled for this instance
 */
export function initSaveResults(calculatorRoot) {
    const context = readSaveResultsContext(calculatorRoot);
    if (!context) return null;

    const modal = new SaveResultsModal(context);

    // Delegated, so buttons injected later (the amortization slot) need no rebinding.
    calculatorRoot.on('click', '[' + BUTTON_MARKER + ']', (e) => {
        e.preventDefault();

        const capture = captureScenario(calculatorRoot, context.calculatorSlug);

        if (!capture) {
            // Nothing has been calculated in this pane, so there is nothing to save. AC-11.
            return;
        }

        modal.open(capture);
    });

    mountSaveResultsButtons(calculatorRoot);

    return () => mountSaveResultsButtons(calculatorRoot);
}
